import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import type {
  CustomItem,
  LocalizedString,
  ScreenAnchor,
  SchemeContent,
  SchemeKnobValues,
  SchemeManifest,
  SchemeToggleKnob,
} from "@shared/types/foyer";
import { CUSTOM_ITEM_MAX, SCREEN_ANCHORS } from "@shared/types/foyer";
import ColorSwatch from "../ColorSwatch";
import LocalizedTextField from "../LocalizedTextField";
import AssetField from "../AssetField";
import { tokens } from "../../theme";
import EditorCard from "./EditorCard";
import { EDITOR_MAX_WIDTH, fieldGrid } from "./editorLayout";

interface Props {
  manifest: SchemeManifest;
  /** Knob values - the screen's NAME lives here, not in content. */
  knobs: SchemeKnobValues | undefined;
  onKnobs: (next: SchemeKnobValues) => void;
  content: SchemeContent;
  onChange: (next: SchemeContent) => void;
  supportedLocales: string[];
  defaultLocale: string;
}

/** Defaults for a brand-new item: readable over any photograph on day one. */
const NEW_ITEM = (n: number): CustomItem => ({
  id: `c${n}-${Math.random().toString(36).slice(2, 8)}`,
  label: `Item ${n}`,
  vignette: { size: 55, color: "#000000" },
  text: {
    body: "",
    placement: "middle-left",
    fontSize: 64,
    color: "#ffffff",
  },
  qr: { url: "", placement: "bottom-right" },
});

/**
 * The Custom screen's editor: the one pane where the operator designs rather
 * than fills in.
 *
 * Each item is one card, because on the TV each item IS one composition - its
 * own picture, vignette, words and QR, swapped in as the resident moves along
 * the bottom bar. Authoring them as four little pages rather than one page with
 * four backgrounds is what keeps the editor honest about what the resident sees.
 */
export default function CustomScreenEditor({
  manifest,
  knobs,
  onKnobs,
  content,
  onChange,
  supportedLocales,
  defaultLocale,
}: Props) {
  const loc = { supportedLocales, defaultLocale };
  const toggle = manifest.knobs.find(
    (k): k is SchemeToggleKnob => k.type === "toggle" && !!k.titleKnobId
  );
  const titleKnob = manifest.knobs.find(
    (k) => k.id === toggle?.titleKnobId && k.type === "text"
  );
  const rawTitle =
    titleKnob && typeof knobs?.[titleKnob.id] === "string"
      ? (knobs[titleKnob.id] as string)
      : "";

  const items = content.custom?.items ?? [];
  const setItems = (next: CustomItem[]) =>
    onChange({ ...content, custom: { ...content.custom, items: next } });
  const patch = (i: number, part: Partial<CustomItem>) =>
    setItems(items.map((it, n) => (n === i ? { ...it, ...part } : it)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      {titleKnob && titleKnob.type === "text" && (
        <EditorCard
          eyebrow="Name"
          data-testid="custom-screen-name"
          hint="What this screen is called - in the menu here and on the TV. Leave it empty to fall back to its default name."
        >
          <TextField
            size="small"
            label={titleKnob.label}
            placeholder={titleKnob.placeholder ?? titleKnob.default}
            value={rawTitle}
            onChange={(e) =>
              onKnobs({ ...(knobs ?? {}), [titleKnob.id]: e.target.value })
            }
            inputProps={{
              maxLength: titleKnob.maxLength,
              "aria-label": titleKnob.label,
            }}
            data-testid="custom-title-field"
            sx={{ maxWidth: 320 }}
          />
        </EditorCard>
      )}

      <EditorCard
        eyebrow={`Items (${items.length}/${CUSTOM_ITEM_MAX})`}
        data-testid="custom-items"
        hint="One to four items sit along the bottom of the screen. Moving between them changes the whole page - each carries its own picture, words and QR."
        action={
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={items.length >= CUSTOM_ITEM_MAX}
            onClick={() => setItems([...items, NEW_ITEM(items.length + 1)])}
            data-testid="custom-add-item"
          >
            Add item
          </Button>
        }
      >
        {items.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: tokens.inkMuted }}>
            No items yet. The screen stays out of the TV menu until it has at
            least one - an empty page is not worth a tile.
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {items.map((item, i) => (
              <ItemCard
                key={item.id}
                item={item}
                index={i}
                count={items.length}
                loc={loc}
                onPatch={(part) => patch(i, part)}
                onMove={(dir) => move(i, dir)}
                onRemove={() => setItems(items.filter((_, n) => n !== i))}
              />
            ))}
          </Stack>
        )}
      </EditorCard>
    </Stack>
  );
}

interface ItemProps {
  item: CustomItem;
  index: number;
  count: number;
  loc: { supportedLocales: string[]; defaultLocale: string };
  onPatch: (part: Partial<CustomItem>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

function ItemCard({
  item,
  index,
  count,
  loc,
  onPatch,
  onMove,
  onRemove,
}: ItemProps) {
  const text = item.text ?? {
    body: "" as LocalizedString,
    placement: "middle-left" as ScreenAnchor,
    fontSize: 64,
    color: "#ffffff",
  };
  const qr = item.qr ?? { url: "", placement: "bottom-right" as ScreenAnchor };
  const vignette = item.vignette ?? { size: 55, color: "#000000" };

  return (
    <Box
      data-testid={`custom-item-${index}`}
      sx={{
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.75,
          py: 0.75,
          bgcolor: tokens.rowAlt,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".09em",
            textTransform: "uppercase",
            color: tokens.inkFaint,
            flex: 1,
          }}
        >
          Item {index + 1}
        </Typography>
        <Tooltip title="Move left">
          <span>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => onMove(-1)}
              aria-label={`Move item ${index + 1} left`}
            >
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move right">
          <span>
            <IconButton
              size="small"
              disabled={index === count - 1}
              onClick={() => onMove(1)}
              aria-label={`Move item ${index + 1} right`}
            >
              <ArrowDownwardIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton
            size="small"
            onClick={onRemove}
            aria-label={`Remove item ${index + 1}`}
            data-testid={`custom-remove-${index}`}
          >
            <DeleteOutlineIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack spacing={2.5} sx={{ p: 2.25 }}>
        <Box sx={fieldGrid(300)}>
          <LocalizedTextField
            label="Menu label"
            value={item.label}
            {...loc}
            onChange={(v) => onPatch({ label: v })}
            helperText="The word on the bottom bar."
            testId={`custom-label-${index}`}
          />
          <AssetField
            label="Background picture"
            value={item.imageUrl ?? ""}
            preview
            onChange={(v) => onPatch({ imageUrl: v || undefined })}
            testId={`custom-image-${index}`}
          />
        </Box>

        <Section
          title="Vignette"
          hint="Darkens the edges so words stay readable over a photograph."
        >
          <Box sx={fieldGrid(280)}>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: tokens.inkMuted }}>
                Size ({vignette.size}%)
              </Typography>
              <Slider
                size="small"
                value={vignette.size}
                min={0}
                max={100}
                onChange={(_, v) =>
                  onPatch({ vignette: { ...vignette, size: v as number } })
                }
                aria-label={`Vignette size, item ${index + 1}`}
                data-testid={`custom-vignette-size-${index}`}
              />
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ColorSwatch
                value={vignette.color}
                onChange={(v) =>
                  onPatch({ vignette: { ...vignette, color: v } })
                }
                ariaLabel={`Vignette colour, item ${index + 1}`}
              />
              <TextField
                size="small"
                label="Vignette colour"
                value={vignette.color}
                onChange={(e) =>
                  onPatch({ vignette: { ...vignette, color: e.target.value } })
                }
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        </Section>

        <Section title="Text">
          <Stack spacing={2}>
            <LocalizedTextField
              label="Text"
              value={text.body}
              multiline
              rows={3}
              {...loc}
              onChange={(v) => onPatch({ text: { ...text, body: v } })}
              testId={`custom-text-${index}`}
            />
            <Box sx={fieldGrid(240)}>
              <AnchorPicker
                label="Placement"
                value={text.placement}
                onChange={(placement) =>
                  onPatch({ text: { ...text, placement } })
                }
                testId={`custom-text-anchor-${index}`}
              />
              <Box>
                <Typography sx={{ fontSize: 12.5, color: tokens.inkMuted }}>
                  Font size ({text.fontSize}px)
                </Typography>
                <Slider
                  size="small"
                  value={text.fontSize}
                  min={24}
                  max={160}
                  step={2}
                  onChange={(_, v) =>
                    onPatch({ text: { ...text, fontSize: v as number } })
                  }
                  aria-label={`Font size, item ${index + 1}`}
                  data-testid={`custom-font-size-${index}`}
                />
                <Typography sx={{ fontSize: 11.5, color: tokens.inkFaint }}>
                  Measured on the 1920×1080 canvas the TV scales from.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ColorSwatch
                  value={text.color}
                  onChange={(v) => onPatch({ text: { ...text, color: v } })}
                  ariaLabel={`Text colour, item ${index + 1}`}
                />
                <TextField
                  size="small"
                  label="Text colour"
                  value={text.color}
                  onChange={(e) =>
                    onPatch({ text: { ...text, color: e.target.value } })
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>
          </Stack>
        </Section>

        <Section
          title="QR code"
          hint="A web address the guest can scan. Leave it empty for no QR."
        >
          <Box sx={fieldGrid(280)}>
            <TextField
              size="small"
              label="Address"
              placeholder="https://…"
              value={qr.url}
              onChange={(e) => onPatch({ qr: { ...qr, url: e.target.value } })}
              data-testid={`custom-qr-url-${index}`}
            />
            <AnchorPicker
              label="Placement"
              value={qr.placement}
              onChange={(placement) => onPatch({ qr: { ...qr, placement } })}
              testId={`custom-qr-anchor-${index}`}
            />
          </Box>
        </Section>
      </Stack>
    </Box>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink }}>
        {title}
      </Typography>
      {hint && (
        <Typography
          sx={{ fontSize: 12, color: tokens.inkFaint, mb: 1, mt: 0.25 }}
        >
          {hint}
        </Typography>
      )}
      <Box sx={{ mt: hint ? 0 : 1 }}>{children}</Box>
    </Box>
  );
}

/**
 * The nine-point placement picker: the grid itself, as buttons.
 *
 * A dropdown listing "middle-left / bottom-centre / …" makes an operator
 * translate words into a picture on every choice. The control IS the screen,
 * so the answer is one look and one click.
 */
function AnchorPicker({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: ScreenAnchor;
  onChange: (next: ScreenAnchor) => void;
  testId?: string;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12.5, color: tokens.inkMuted, mb: 0.75 }}>
        {label}
      </Typography>
      <Box
        data-testid={testId}
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "3px",
          width: 132,
          aspectRatio: "16 / 9",
          p: "3px",
          border: "1px solid",
          borderColor: tokens.divider,
          borderRadius: "8px",
          bgcolor: tokens.rowAlt,
        }}
      >
        {SCREEN_ANCHORS.map((anchor) => (
          <Box
            key={anchor}
            component="button"
            type="button"
            role="radio"
            aria-checked={anchor === value}
            aria-label={anchor.replace("-", " ")}
            data-testid={`${testId}-${anchor}`}
            onClick={() => onChange(anchor)}
            sx={{
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              p: 0,
              bgcolor: anchor === value ? tokens.brand : tokens.surface,
              opacity: anchor === value ? 1 : 0.65,
              "&:hover": { opacity: 1 },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
